import { DataStoreState } from "./dataStore";
import { AFMessage, AFMessageType, AFMessageTypeList, TokenState, afMessageTypeToStoreKey, AFMessageRawData, AFMessageStateData, AlertType } from "./types";
import { DevCluster, DevClusterRawData } from "./types/devcluster";
import { Ranking, RankingRawData } from "./types/ranking";
import { Statistics, StatisticsRawData } from "./types/statistics";
import { TokenInfo, TokenInfoRawData } from "./types/tokenInfo";
import { FeTokenState, FeTokenStateRawData } from "./types/feTokenState";
import { FeTokenAlert, FeTokenAlertRawData, FeTokenAlertStateData } from "./types/feTokenAlert";
import { PriceTracker, PriceTrackerRawData } from "./types/priceTracker";
import { DexScreener, DexScreenerRawData } from "./types/dexScreener";
import { chromeNotification } from "../../utils/notification";
import { setHash } from "../../utils/hashRouting";

export class DataAnalyzer {
    private dataQueue: AFMessage[];
    private getStoreState: () => DataStoreState;
    
    // 배치 처리 관련 설정
    private readonly BATCH_SIZE = 50; // 배치 크기
    private readonly BATCH_TIMEOUT_MS = 100; // 배치 타임아웃 (밀리초)
    private batchTimeoutId: NodeJS.Timeout | null = null;
    private isProcessingBatch = false;

    constructor(getStoreState: () => DataStoreState) {
        this.dataQueue = [];
        this.getStoreState = getStoreState;
    }

    static create(getStoreState: () => DataStoreState): DataAnalyzer {
        return new DataAnalyzer(getStoreState);
    }

    isAFMessageType(afDataItem: AFMessage): boolean {
        const { type } = afDataItem;
        return AFMessageTypeList.includes(type);
    }

    createAFDataObject(type: AFMessageType, data: AFMessageRawData | AFMessageStateData | null, timestamp: number): any {
        switch (type) {
            case AFMessageType.TOKEN_INFO:
                return TokenInfo.create(data as TokenInfoRawData, new Date(timestamp));
            case AFMessageType.AF_RANK:
                return Ranking.create(data as RankingRawData, new Date(timestamp));
            case AFMessageType.TOKEN_STATISTICS:
                return Statistics.create(data as StatisticsRawData, new Date(timestamp));
            case AFMessageType.DEV_CLUSTER_COUNT:
                return DevCluster.create(data as DevClusterRawData, new Date(timestamp));
            case AFMessageType.PRICE_TRACKER:
                return PriceTracker.create(this.parsePriceTrackerData(data as any), new Date(timestamp));
            case AFMessageType.FE_TOKEN_STATE:
                return FeTokenState.create(this.parseFeTokenStateData(data as any), new Date(timestamp));
            case AFMessageType.FE_TOKEN_ALERT:
                return FeTokenAlert.create(data as FeTokenAlertRawData, new Date(timestamp));
            case AFMessageType.DEX_SCREENER:
                return DexScreener.create(this.parseDexScreenerData(data as any), new Date(timestamp));
            default:
                throw new Error(`Invalid AFMessageType: ${type}`);
        }
    }

    createTokenState(tokenAddress: string, afMessages: AFMessage[]): TokenState {
        if (!afMessages || afMessages.length === 0) {
            throw new Error('afMessages cannot be empty');
        }

        const tokenData: Partial<TokenState> = {
            tokenInfo: this.createAFDataObject(AFMessageType.TOKEN_INFO, null, 0).toStateData(),
            ranking: this.createAFDataObject(AFMessageType.AF_RANK, null, 0).toStateData(),
            statistics: this.createAFDataObject(AFMessageType.TOKEN_STATISTICS, null, 0).toStateData(),
            devCluster: this.createAFDataObject(AFMessageType.DEV_CLUSTER_COUNT, null, 0).toStateData(),
            priceTracker: this.createAFDataObject(AFMessageType.PRICE_TRACKER, null, 0).toStateData(),
            feTokenState: this.createAFDataObject(AFMessageType.FE_TOKEN_STATE, null, 0).toStateData(),
            dexScreener: this.createAFDataObject(AFMessageType.DEX_SCREENER, null, 0).toStateData(),
            feTokenAlert: {},
            hasOrderIssue: false,
        };

        // FE_TOKEN_ALERT 메시지들을 별도로 처리하여 객체 형태로 변환
        const alertMap: Partial<Record<AlertType, FeTokenAlertStateData>> = {};

        afMessages.forEach((message) => {
            // FE_TOKEN_ALERT는 별도로 처리
            if (message.type === AFMessageType.FE_TOKEN_ALERT) {
                const alertObject = this.createAFDataObject(message.type, message.data, message.timestamp) as FeTokenAlert;
                const alertState = alertObject.toStateData();
                // 같은 타입의 alert가 여러 개 있으면 가장 최신 것만 유지
                const existingAlert = alertMap[alertState.type];
                if (!existingAlert || alertObject.filter(existingAlert)) {
                    alertMap[alertState.type] = alertState;
                }
            } else {
                const afDataObject = this.createAFDataObject(message.type, message.data, message.timestamp);
                tokenData[afMessageTypeToStoreKey[message.type] as keyof TokenState] = afDataObject?.toStateData();
                
                // Statistics 메시지인 경우 hasOrderIssue 계산
                if (message.type === AFMessageType.TOKEN_STATISTICS) {
                    tokenData.hasOrderIssue = (afDataObject as Statistics).hasOrderIssue();
                }
            }
        });

        // alertMap을 feTokenAlert에 할당
        tokenData.feTokenAlert = alertMap;

        return {
            ...tokenData as TokenState,
            tokenAddress,
            lastUpdated: afMessages[afMessages.length - 1].timestamp,
        };
    }

    addToQueue(data: AFMessage[]) {
        // 배열 스프레드 대신 push 사용 (메모리 효율 개선)
        this.dataQueue.push(...data);
        
        // 초기화 체크 후 스케줄링 (조건 체크 최적화)
        if (this.getStoreState().isInitialized) {
            this.scheduleBatchProcessing();
        }
    }

    /**
     * 배치 처리를 스케줄링합니다.
     * 배치 크기에 도달하거나 타임아웃이 지나면 처리합니다.
     */
    private scheduleBatchProcessing() {
        // 이미 처리 중이면 스케줄링하지 않음 (가장 빠른 early return)
        if (this.isProcessingBatch) {
            return;
        }

        const queueLength = this.dataQueue.length;

        // 배치 크기에 도달했으면 즉시 처리 (타임아웃 취소)
        if (queueLength >= this.BATCH_SIZE) {
            // 타임아웃이 있으면 취소 (배치 크기 우선)
            if (this.batchTimeoutId) {
                clearTimeout(this.batchTimeoutId);
                this.batchTimeoutId = null;
            }
            this.processBatch('BATCH_SIZE');
            return;
        }

        // 타임아웃이 이미 설정되어 있으면 리셋하지 않음 (타임아웃이 실행되도록 함)
        if (this.batchTimeoutId) {
            return;
        }

        // 타임아웃 설정: 일정 시간 후에도 배치 크기에 도달하지 않으면 처리
        this.batchTimeoutId = setTimeout(() => {
            this.batchTimeoutId = null;
            // 큐가 비어있지 않고 처리 중이 아닐 때만 실행
            if (this.dataQueue.length > 0 && !this.isProcessingBatch) {
                this.processBatch('TIMEOUT');
            }
        }, this.BATCH_TIMEOUT_MS);
    }

    /**
     * 배치를 처리합니다.
     * 같은 토큰+타입의 메시지는 최신 것만 유지하여 중복 제거합니다.
     */
    private processBatch(trigger: 'BATCH_SIZE' | 'TIMEOUT' = 'BATCH_SIZE') {
        if (this.isProcessingBatch || this.dataQueue.length === 0) {
            return;
        }

        // 타임아웃 정리
        if (this.batchTimeoutId) {
            clearTimeout(this.batchTimeoutId);
            this.batchTimeoutId = null;
        }

        this.isProcessingBatch = true;

        // requestAnimationFrame을 사용하여 브라우저 렌더링 사이클에 맞춰 처리
        requestAnimationFrame(() => {
            try {
                // 배치 크기만큼만 가져오기
                const batch = this.dataQueue.splice(0, this.BATCH_SIZE);
                
                // 같은 토큰+타입의 메시지는 최신 것만 유지 (중복 제거)
                const deduplicatedBatch = this.deduplicateMessages(batch);
                
                // 처리 가능한 메시지만 필터링
                const storeState = this.getStoreState();
                const tokenStateMap = storeState.tokenStateMap;
                
                const processableMessages: AFMessage[] = [];
                const remainingMessages: AFMessage[] = [];
                
                for (const item of deduplicatedBatch) {
                    const existingData = tokenStateMap[item.tokenAddress];
                    const canProcess = 
                        item.type === AFMessageType.TOKEN_INFO || 
                        existingData?.tokenInfo;
                    
                    if (canProcess) {
                        processableMessages.push(item);
                    } else {
                        remainingMessages.push(item);
                    }
                }
                
                // 처리 가능한 메시지들을 일괄 처리
                if (processableMessages.length > 0) {
                    // 같은 토큰별로 그룹화하여 한 번에 업데이트
                    const messagesByToken = this.groupMessagesByToken(processableMessages);
                    
                    // 각 토큰별로 일괄 업데이트
                    for (const [tokenAddress, messages] of Object.entries(messagesByToken)) {
                        this.processBatchForToken(tokenAddress, messages);
                    }
                }
                
                // 처리 불가능한 메시지는 큐 앞쪽에 다시 추가
                if (remainingMessages.length > 0) {
                    this.dataQueue = [...remainingMessages, ...this.dataQueue];
                }
                
                // 남은 큐가 있으면 다음 배치 스케줄링
                if (this.dataQueue.length > 0) {
                    this.scheduleBatchProcessing();
                }
            } finally {
                this.isProcessingBatch = false;
            }
        });
    }

    /**
     * 같은 토큰+타입의 메시지 중 최신 것만 유지합니다.
     */
    private deduplicateMessages(messages: AFMessage[]): AFMessage[] {
        const messageMap = new Map<string, AFMessage>();
        
        // timestamp 기준으로 정렬 (오래된 것부터)
        const sortedMessages = [...messages].sort((a, b) => a.timestamp - b.timestamp);
        
        for (const message of sortedMessages) {
            const key = `${message.tokenAddress}:${message.type}`;
            // 같은 키가 있으면 덮어쓰기 (최신 것만 유지)
            messageMap.set(key, message);
        }
        
        return Array.from(messageMap.values());
    }

    /**
     * 메시지들을 토큰 주소별로 그룹화합니다.
     */
    private groupMessagesByToken(messages: AFMessage[]): Record<string, AFMessage[]> {
        const grouped: Record<string, AFMessage[]> = {};
        
        for (const message of messages) {
            if (!grouped[message.tokenAddress]) {
                grouped[message.tokenAddress] = [];
            }
            grouped[message.tokenAddress].push(message);
        }
        
        return grouped;
    }

    /**
     * 특정 토큰에 대한 메시지들을 일괄 처리합니다.
     */
    private processBatchForToken(tokenAddress: string, messages: AFMessage[]) {
        // 타입별로 정렬하여 TOKEN_INFO를 먼저 처리
        const sortedMessages = messages.sort((a, b) => {
            if (a.type === AFMessageType.TOKEN_INFO) return -1;
            if (b.type === AFMessageType.TOKEN_INFO) return 1;
            return 0;
        });
        
        // 각 메시지를 순차적으로 처리
        for (const message of sortedMessages) {
            this.processAFData(tokenAddress, message);
        }
    }

    /**
     * 큐를 확인하고 배치 처리를 스케줄링합니다.
     * 초기화 완료 후 호출됩니다.
     */
    checkQueue() {
        const storeState = this.getStoreState();
        
        if (storeState.isInitialized && this.dataQueue.length > 0) {
            this.scheduleBatchProcessing();
        }
    }

    processAFData(tokenAddress: string, item: AFMessage) {
        const storeState = this.getStoreState();

        const existingData = storeState.tokenStateMap[tokenAddress] as any;
        const messageType = item.type as AFMessageType;
        
        // FE_TOKEN_ALERT는 객체로 처리 (각 타입별로 최신 값 하나만 저장)
        if (messageType === AFMessageType.FE_TOKEN_ALERT) {
            const newAlertObject = this.createAFDataObject(messageType, item.data, item.timestamp) as FeTokenAlert;
            const newAlertState = newAlertObject.toStateData();
            
            if (existingData) {
                const existingAlerts: Partial<Record<AlertType, FeTokenAlertStateData>> = existingData.feTokenAlert || {};
                const existingAlertOfType = existingAlerts[newAlertState.type];
                
                // 같은 type의 alert가 있으면 더 최신인 경우만 업데이트, 없으면 추가
                if (!existingAlertOfType || newAlertObject.filter(existingAlertOfType)) {
                    const updatedAlerts = {
                        ...existingAlerts,
                        [newAlertState.type]: newAlertState,
                    };
                    storeState.updateTokenStateMap(tokenAddress, 'feTokenAlert', updatedAlerts);
                    
                    // 새로운 alert가 추가된 경우에만 notification 표시
                    if (!existingAlertOfType) {
                        this.showAlertNotification(tokenAddress, newAlertState, existingData);
                    }
                }
            } else {
                const newState = this.createTokenState(tokenAddress, [item]);
                for (const stateKey of Object.keys(newState) as (keyof TokenState)[]) {
                    storeState.updateTokenStateMap(tokenAddress, stateKey, newState[stateKey] as any);
                }
                
                // 새로운 alert가 추가된 경우 notification 표시
                this.showAlertNotification(tokenAddress, newAlertState, newState);
            }
        } else {
            const stateKey = afMessageTypeToStoreKey[messageType] as "tokenInfo" | "ranking" | "statistics" | "devCluster" | "priceTracker" | "dexScreener" | "feTokenState";
            
            if (existingData) {
                const existingDataObject = this.createAFDataObject(messageType, existingData[stateKey], existingData.lastUpdated);
                const newDataObject = this.createAFDataObject(messageType, item.data, item.timestamp);
                let shouldProcess = newDataObject.filter(existingDataObject);
                if (shouldProcess && newDataObject) {
                    const newState = newDataObject.toStateData();
                    storeState.updateTokenStateMap(tokenAddress, stateKey, newState);
                    
                    // Statistics 업데이트 시 hasOrderIssue 계산 및 저장
                    if (messageType === AFMessageType.TOKEN_STATISTICS) {
                        const hasOrderIssue = (newDataObject as Statistics).hasOrderIssue();
                        storeState.updateTokenStateMap(tokenAddress, 'hasOrderIssue', hasOrderIssue);
                    }
                }
            } else {
                const newState = this.createTokenState(tokenAddress, [item]);
                for (const stateKey of Object.keys(newState) as (keyof TokenState)[]) {
                    storeState.updateTokenStateMap(tokenAddress, stateKey, newState[stateKey] as any);
                }
                
                // Statistics 생성 시 hasOrderIssue 계산 및 저장
                if (messageType === AFMessageType.TOKEN_STATISTICS) {
                    const statisticsObject = this.createAFDataObject(messageType, item.data, item.timestamp) as Statistics;
                    const hasOrderIssue = statisticsObject.hasOrderIssue();
                    storeState.updateTokenStateMap(tokenAddress, 'hasOrderIssue', hasOrderIssue);
                }
            }
        }
    }

    /**
     * PRICE_TRACKER 스트림 데이터를 파싱합니다.
     * ISO 문자열로 오는 timestamp를 Date 객체로 변환합니다.
     */
    private parsePriceTrackerData(data: any): PriceTrackerRawData | null {
        if (!data) {
            return null;
        }

        // historical 데이터 파싱
        const parseHistoricalPoint = (point: any) => {
            if (!point) return null;
            return {
                mcap: point.mcap,
                timestamp: point.timestamp instanceof Date 
                    ? point.timestamp 
                    : new Date(point.timestamp),
            };
        };

        return {
            mcap: data.mcap || 0,
            timestamp: data.timestamp instanceof Date 
                ? data.timestamp 
                : new Date(data.timestamp || 0),
            ath_mcap: data.ath_mcap || 0,
            ath_ts: data.ath_ts instanceof Date 
                ? data.ath_ts 
                : new Date(data.ath_ts || 0),
            historical: {
                '1h': parseHistoricalPoint(data.historical?.['1h']),
                '3h': parseHistoricalPoint(data.historical?.['3h']),
                '6h': parseHistoricalPoint(data.historical?.['6h']),
                '12h': parseHistoricalPoint(data.historical?.['12h']),
                '24h': parseHistoricalPoint(data.historical?.['24h']),
                '3d': parseHistoricalPoint(data.historical?.['3d']),
            },
        };
    }

    /**
     * DEX_SCREENER 스트림 데이터를 파싱합니다.
     * ISO 문자열로 오는 date, timestamp, claimDate를 Date 객체로 변환합니다.
     */
    private parseDexScreenerData(data: any): DexScreenerRawData | null {
        if (!data) {
            return null;
        }

        // adHistories 파싱
        const parseAdHistories = (histories: any[]): any[] => {
            if (!Array.isArray(histories)) return [];
            return histories.map(history => ({
                date: history.date instanceof Date 
                    ? history.date 
                    : new Date(history.date),
                type: history.type || '',
                impressions: history.impressions || 0,
            }));
        };

        // totalBoostsHistory 파싱
        const parseTotalBoostsHistory = (histories: any[]): any[] => {
            if (!Array.isArray(histories)) return [];
            return histories.map(history => ({
                timestamp: history.timestamp instanceof Date 
                    ? history.timestamp 
                    : new Date(history.timestamp),
                totalAmount: history.totalAmount || 0,
                amount: history.amount || 0,
            }));
        };

        // ctoHistories 파싱
        const parseCtoHistories = (histories: any[]): any[] => {
            if (!Array.isArray(histories)) return [];
            return histories.map(history => ({
                timestamp: history.timestamp instanceof Date 
                    ? history.timestamp 
                    : new Date(history.timestamp),
                claimDate: history.claimDate instanceof Date 
                    ? history.claimDate 
                    : new Date(history.claimDate),
            }));
        };

        return {
            tokenAddress: data.tokenAddress || '',
            adHistories: parseAdHistories(data.adHistories || []),
            totalBoostsHistory: parseTotalBoostsHistory(data.totalBoostsHistory || []),
            ctoHistories: parseCtoHistories(data.ctoHistories || []),
            links: data.links || [],
            lastUpdatedAt: data.lastUpdatedAt || 0,
        };
    }

    /**
     * Alert notification 표시
     */
    private showAlertNotification(
        tokenAddress: string,
        alert: FeTokenAlertStateData,
        tokenData: TokenState | any
    ): void {
        try {
            const tokenSymbol = tokenData?.tokenInfo?.tokenSymbol || 'Unknown';
            const tokenImage = tokenData?.tokenInfo?.imageUrl || '';
            
            // Alert 타입별 제목과 메시지 구성
            const getAlertTitle = (alertType: AlertType): string => {
                switch (alertType) {
                    case AlertType.AF_RANK:
                        return '🚀 AF Rank Alert';
                    case AlertType.TWEET_COUNT:
                        return '📱 Tweet Count Alert';
                    case AlertType.HIGH_VALUE_TARGET_MENTION:
                        return '⭐ High Value Target Mention';
                    case AlertType.DEXSCREENER_CTO:
                        return '🎯 DexScreener CTO';
                    case AlertType.DEXSCREENER_BOOST:
                        return '🚀 DexScreener Boost';
                    default:
                        return '🔔 New Alert';
                }
            };

            const title = `${getAlertTitle(alert.type)} - $${tokenSymbol}`;
            const body = alert.description || '새로운 알림이 발생했습니다.';

            chromeNotification.showNotification(
                title,
                tokenImage,
                {
                    body,
                    tag: `alert-${tokenAddress}-${alert.type}`,
                    requireInteraction: false,
                },
                () => {
                    // 알림 클릭 시 토큰 모달로 이동
                    setHash(tokenAddress, 'token');
                }
            );
        } catch (error) {
            console.error('Alert notification 표시 실패:', error);
        }
    }

    /**
     * FE_TOKEN_STATE 스트림 데이터를 파싱합니다.
     */
    private parseFeTokenStateData(data: any): FeTokenStateRawData | null {
        if (!data) {
            return null;
        }

        return {
            tokenAddress: data.tokenAddress || '',
            isArchived: data.isArchived || false,
            archivedAt: data.archivedAt,
            category: data.category,
            memo: data.memo,
            status: data.status,
            contentsExists: data.contentsExists,
            totalScore: data.totalScore,
        };
    }
}
